/**
 * ThemeToggle Component
 * UI control for switching between visual themes
 * Provides accessible theme switching with keyboard support
 */

import { useTheme } from '../../hooks/useTheme';

/**
 * ThemeToggle Props
 */
interface ThemeToggleProps {
  /** Optional CSS class name for styling */
  className?: string;
}

/**
 * ThemeToggle Component
 * 
 * Renders a sliding toggle switch that toggles between retro and modern themes.
 * Displays theme-specific icons with smooth sliding animation.
 * Meets WCAG 2.1 Level AA accessibility standards.
 * 
 * @param props - Component props
 * @returns Theme toggle switch component
 * 
 * @example
 * ```tsx
 * <ThemeToggle />
 * ```
 */
export function ThemeToggle({ className = '' }: ThemeToggleProps) {
  const { theme, toggleTheme } = useTheme();

  /**
   * Handle keyboard events for accessibility
   * Allows Enter and Space keys to toggle theme
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleTheme();
    }
  };

  return (
    <button
      onClick={toggleTheme}
      onKeyDown={handleKeyDown}
      className={`theme-toggle-switch ${theme} ${className}`}
      aria-label={`Toggle theme. Current theme: ${theme}`}
      title={`Switch to ${theme === 'retro' ? 'modern' : 'retro'} theme`}
      type="button"
      role="switch"
      aria-checked={theme === 'modern'}
    >
      <span className="theme-toggle-track">
        <span className="theme-toggle-thumb">
          <span className="theme-toggle-icon" aria-hidden="true">
            {theme === 'retro' ? '🎨' : '🎵'}
          </span>
        </span>
      </span>
    </button>
  );
}
